import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDataCache } from '../contexts/DataCacheContext';
import configService from '../services/configService';
import DashboardLayout from './DashboardLayout';
import { useToast } from './Toast';

function Skills() {
  const { user } = useAuth();
  const { cache, prefetchSkills } = useDataCache();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const { showToast } = useToast();

  const moduleDisplayNames = {
    'booking_system': 'Appointment Manager',
    'pricing_quotes': 'Service Catalog',
    'call_routing': 'Smart Dispatch',
    'staff_dispatch': 'Staff Dispatch',
    'faq_knowledge': 'Business Knowledge Base'
  };

  useEffect(() => {
    if (!cache.skills) {
      prefetchSkills();
    }
  }, []);

  useEffect(() => {
    if (cache.skills?.modules) {
      setModules(cache.skills.modules);
      setLoading(false);
    } else {
      loadModules();
    }
  }, [cache.skills?.modules]);

  const loadModules = async () => {
    try {
      const response = await configService.getModules();
      const moduleData = response.modules || [];
      setModules(moduleData);
    } catch (error) {
      console.error('Failed to load modules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (moduleId, currentEnabled) => {
    const newEnabled = !currentEnabled;

    // Dependency check: Disabling Service Catalog when Appointment Manager is enabled
    if (moduleId === 'pricing_quotes' && !newEnabled) {
      const bookingModule = modules.find(m => m.module_id === 'booking_system');
      if (bookingModule?.enabled) {
        showToast('Please disable Appointment Manager first before disabling Service Catalog.');
        return;
      }
    }

    setSaving(moduleId);
    try {
      await configService.updateModule(moduleId, newEnabled);
      
      // Auto-enable Service Catalog when enabling Appointment Manager
      if (moduleId === 'booking_system' && newEnabled) {
        const pricingModule = modules.find(m => m.module_id === 'pricing_quotes');
        if (pricingModule && !pricingModule.enabled) {
          await configService.updateModule('pricing_quotes', true);
        }
      }

      const response = await configService.getModules();
      const moduleData = response.modules || [];
      setModules(moduleData);
      cache.skills = { modules: moduleData };
      showToast('Saved successfully!');
    } catch (error) {
      console.error('Failed to update module:', error);
      showToast('Failed to save', 'error');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div>
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="mb-6">
            <div className="text-4xl font-bold text-gray-900 mb-2">Assistant Skills</div>
            <p className="text-sm text-gray-600">Enable or disable capabilities for your AI assistant</p>
          </div>

        <div className="space-y-4">
          {modules.map((module) => (
            <div key={module.module_id} className="bg-white p-6 rounded-xl shadow-sm border flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {moduleDisplayNames[module.module_id] || module.name}
                </h3>
                <p className="text-gray-600">{module.description}</p>
                <p className="text-sm text-gray-500 mt-2">{module.function_count} functions available</p>
              </div>
              <button
                onClick={() => handleToggle(module.module_id, module.enabled)}
                disabled={saving === module.module_id}
                className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  module.enabled ? 'bg-blue-600' : 'bg-gray-200'
                } ${saving === module.module_id ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                    module.enabled ? 'translate-x-7' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>

          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">💡 Skill Dependencies</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Appointment Manager requires Service Catalog to be enabled</li>
              <li>• Service Catalog can be used independently for pricing quotes</li>
              <li>• Smart Dispatch works independently for call routing</li>
              <li>• Staff Dispatch works independently for SMS notifications to staff</li>
              <li>• Business Knowledge Base works independently — requires a FAQ URL to be indexed first</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default Skills;
